<template>
  <div id="maternal-assessment" class="container-fluid">
    <!-- Header -->
    <pnc_full_head />

    <!-- Main Content Section -->
    <div class="main-content row g-4">
      <!-- Left Column: Assessment Forms -->
      <div class="col-lg-8">
        <!-- Vital Signs Card -->
        <div class="card mb-4">
          <div class="card-header bg-primary text-white">
            <h3 class="mb-0">
              <i class="fas fa-heartbeat"></i> Vital Signs Assessment
            </h3>
          </div>
          <div class="card-body">
            <div class="row g-4">
              <!-- Blood Pressure -->
              <div class="col-md-6">
                <div class="assessment-item">
                  <h4 class="section-title">
                    <i class="fas fa-tachometer-alt"></i> Blood Pressure
                  </h4>
                  <div class="vitals-grid">
                    <div class="vital-input-group">
                      <label>Systolic (mmHg)</label>
                      <div class="input-group">
                        <input
                          type="number"
                          class="form-control"
                          v-model="assessment.vitalSigns.bloodPressure.systolic"
                          placeholder="120"
                          min="60"
                          max="200"
                        />
                        <span class="input-group-text">mmHg</span>
                      </div>
                    </div>
                    <div class="vital-input-group">
                      <label>Diastolic (mmHg)</label>
                      <div class="input-group">
                        <input
                          type="number"
                          class="form-control"
                          v-model="
                            assessment.vitalSigns.bloodPressure.diastolic
                          "
                          placeholder="80"
                          min="40"
                          max="120"
                        />
                        <span class="input-group-text">mmHg</span>
                      </div>
                    </div>
                    <div
                      class="vital-status"
                      :class="bloodPressureStatus.class"
                    >
                      <i :class="bloodPressureStatus.icon"></i>
                      {{ bloodPressureStatus.text }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- Temperature -->
              <div class="col-md-6">
                <div class="assessment-item">
                  <h4 class="section-title">
                    <i class="fas fa-thermometer-half"></i> Temperature
                  </h4>
                  <div class="vitals-grid">
                    <div class="vital-input-group">
                      <label>Temperature (°C)</label>
                      <div class="input-group">
                        <input
                          type="number"
                          class="form-control"
                          v-model="assessment.vitalSigns.temperature.value"
                          placeholder="36.5"
                          step="0.1"
                          min="35"
                          max="42"
                        />
                        <span class="input-group-text">°C</span>
                      </div>
                    </div>
                    <div class="vital-input-group">
                      <label>Route</label>
                      <select
                        class="form-select"
                        v-model="assessment.vitalSigns.temperature.route"
                      >
                        <option value="oral">Oral</option>
                        <option value="axillary">Axillary</option>
                        <option value="tympanic">Tympanic</option>
                        <option value="rectal">Rectal</option>
                      </select>
                    </div>
                    <div class="vital-status" :class="temperatureStatus.class">
                      <i :class="temperatureStatus.icon"></i>
                      {{ temperatureStatus.text }}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Additional Vital Signs -->
            <div class="row g-4 mt-2">
              <div class="col-md-4">
                <div class="vital-card">
                  <div class="vital-icon">
                    <i class="fas fa-heart"></i>
                  </div>
                  <div class="vital-content">
                    <label>Heart Rate</label>
                    <div class="input-group">
                      <input
                        type="number"
                        class="form-control"
                        v-model="assessment.vitalSigns.heartRate"
                        placeholder="72"
                      />
                      <span class="input-group-text">bpm</span>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-md-4">
                <div class="vital-card">
                  <div class="vital-icon">
                    <i class="fas fa-lungs"></i>
                  </div>
                  <div class="vital-content">
                    <label>Respiratory Rate</label>
                    <div class="input-group">
                      <input
                        type="number"
                        class="form-control"
                        v-model="assessment.vitalSigns.respiratoryRate"
                        placeholder="16"
                      />
                      <span class="input-group-text">/min</span>
                    </div>
                  </div>
                </div>
              </div>
              <div class="col-md-4">
                <div class="vital-card">
                  <div class="vital-icon">
                    <i class="fas fa-weight"></i>
                  </div>
                  <div class="vital-content">
                    <label>Weight</label>
                    <div class="input-group">
                      <input
                        type="number"
                        class="form-control"
                        v-model="assessment.vitalSigns.weight"
                        placeholder="65.5"
                        step="0.1"
                      />
                      <span class="input-group-text">kg</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Uterine & Lochia Assessment -->
        <div class="card mb-4">
          <div class="card-header bg-info text-white">
            <h3 class="mb-0">
              <i class="fas fa-female"></i> Uterine & Lochia Assessment
            </h3>
          </div>
          <div class="card-body">
            <div class="row g-4">
              <!-- Uterine Involution -->
              <div class="col-md-6">
                <div class="assessment-item">
                  <h4 class="section-title">
                    <i class="fas fa-uterus"></i> Uterine Involution
                  </h4>
                  <div class="assessment-grid">
                    <div class="form-group">
                      <label>Fundal Height (weeks)</label>
                      <div class="input-group">
                        <input
                          type="number"
                          class="form-control"
                          v-model="assessment.uterineInvolution.fundalHeight"
                          placeholder="16"
                          min="0"
                          max="40"
                        />
                        <span class="input-group-text">weeks</span>
                      </div>
                    </div>
                    <div class="form-group">
                      <label>Fundal Position</label>
                      <select
                        class="form-select"
                        v-model="assessment.uterineInvolution.position"
                      >
                        <option value="midline">Midline</option>
                        <option value="deviated_right">Deviated Right</option>
                        <option value="deviated_left">Deviated Left</option>
                        <option value="contracted">Well Contracted</option>
                      </select>
                    </div>
                    <div class="form-group">
                      <label>Tenderness</label>
                      <div class="btn-group w-100" role="group">
                        <button
                          type="button"
                          class="btn"
                          :class="
                            assessment.uterineInvolution.tenderness === 'none'
                              ? 'btn-success'
                              : 'btn-outline-success'
                          "
                          @click="
                            assessment.uterineInvolution.tenderness = 'none'
                          "
                        >
                          None
                        </button>
                        <button
                          type="button"
                          class="btn"
                          :class="
                            assessment.uterineInvolution.tenderness === 'mild'
                              ? 'btn-warning'
                              : 'btn-outline-warning'
                          "
                          @click="
                            assessment.uterineInvolution.tenderness = 'mild'
                          "
                        >
                          Mild
                        </button>
                        <button
                          type="button"
                          class="btn"
                          :class="
                            assessment.uterineInvolution.tenderness ===
                            'moderate'
                              ? 'btn-danger'
                              : 'btn-outline-danger'
                          "
                          @click="
                            assessment.uterineInvolution.tenderness = 'moderate'
                          "
                        >
                          Moderate
                        </button>
                        <button
                          type="button"
                          class="btn"
                          :class="
                            assessment.uterineInvolution.tenderness === 'severe'
                              ? 'btn-danger'
                              : 'btn-outline-danger'
                          "
                          @click="
                            assessment.uterineInvolution.tenderness = 'severe'
                          "
                        >
                          Severe
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Lochia Status -->
              <div class="col-md-6">
                <div class="assessment-item">
                  <h4 class="section-title">
                    <i class="fas fa-tint"></i> Lochia Status
                  </h4>
                  <div class="assessment-grid">
                    <div class="form-group">
                      <label>Color</label>
                      <select
                        class="form-select"
                        v-model="assessment.lochia.color"
                      >
                        <option value="rubra">Rubra (Red)</option>
                        <option value="serosa">Serosa (Pink/Brown)</option>
                        <option value="alba">Alba (White/Yellow)</option>
                        <option value="mixed">Mixed</option>
                      </select>
                    </div>
                    <div class="form-group">
                      <label>Amount</label>
                      <div class="btn-group w-100" role="group">
                        <button
                          type="button"
                          class="btn"
                          :class="
                            assessment.lochia.amount === 'scant'
                              ? 'btn-info'
                              : 'btn-outline-info'
                          "
                          @click="assessment.lochia.amount = 'scant'"
                        >
                          Scant
                        </button>
                        <button
                          type="button"
                          class="btn"
                          :class="
                            assessment.lochia.amount === 'light'
                              ? 'btn-info'
                              : 'btn-outline-info'
                          "
                          @click="assessment.lochia.amount = 'light'"
                        >
                          Light
                        </button>
                        <button
                          type="button"
                          class="btn"
                          :class="
                            assessment.lochia.amount === 'moderate'
                              ? 'btn-warning'
                              : 'btn-outline-warning'
                          "
                          @click="assessment.lochia.amount = 'moderate'"
                        >
                          Moderate
                        </button>
                        <button
                          type="button"
                          class="btn"
                          :class="
                            assessment.lochia.amount === 'heavy'
                              ? 'btn-danger'
                              : 'btn-outline-danger'
                          "
                          @click="assessment.lochia.amount = 'heavy'"
                        >
                          Heavy
                        </button>
                      </div>
                    </div>
                    <div class="form-group">
                      <label>Odor</label>
                      <div class="btn-group w-100" role="group">
                        <button
                          type="button"
                          class="btn"
                          :class="
                            assessment.lochia.odor === 'normal'
                              ? 'btn-success'
                              : 'btn-outline-success'
                          "
                          @click="assessment.lochia.odor = 'normal'"
                        >
                          Normal
                        </button>
                        <button
                          type="button"
                          class="btn"
                          :class="
                            assessment.lochia.odor === 'foul'
                              ? 'btn-danger'
                              : 'btn-outline-danger'
                          "
                          @click="assessment.lochia.odor = 'foul'"
                        >
                          Foul
                        </button>
                      </div>
                    </div>
                    <div class="form-group">
                      <label>Clots</label>
                      <div class="btn-group w-100" role="group">
                        <button
                          type="button"
                          class="btn"
                          :class="
                            assessment.lochia.clots === 'none'
                              ? 'btn-success'
                              : 'btn-outline-success'
                          "
                          @click="assessment.lochia.clots = 'none'"
                        >
                          None
                        </button>
                        <button
                          type="button"
                          class="btn"
                          :class="
                            assessment.lochia.clots === 'small'
                              ? 'btn-warning'
                              : 'btn-outline-warning'
                          "
                          @click="assessment.lochia.clots = 'small'"
                        >
                          Small
                        </button>
                        <button
                          type="button"
                          class="btn"
                          :class="
                            assessment.lochia.clots === 'large'
                              ? 'btn-danger'
                              : 'btn-outline-danger'
                          "
                          @click="assessment.lochia.clots = 'large'"
                        >
                          Large
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Breast & Wound Assessment -->
        <div class="card mb-4">
          <div class="card-header bg-warning text-dark">
            <h3 class="mb-0">
              <i class="fas fa-heart"></i> Breast & Wound Assessment
            </h3>
          </div>
          <div class="card-body">
            <div class="row g-4">
              <!-- Breast Condition -->
              <div class="col-md-6">
                <div class="assessment-item">
                  <h4 class="section-title">
                    <i class="fas fa-utensils"></i> Breast Condition
                  </h4>
                  <div class="assessment-grid">
                    <div class="form-group">
                      <label>Breast Exam</label>
                      <div class="btn-group-vertical w-100" role="group">
                        <button
                          type="button"
                          class="btn text-start"
                          :class="
                            assessment.breast.engorgement
                              ? 'btn-warning'
                              : 'btn-outline-secondary'
                          "
                          @click="
                            assessment.breast.engorgement = !assessment.breast
                              .engorgement
                          "
                        >
                          <i
                            class="fas"
                            :class="
                              assessment.breast.engorgement
                                ? 'fa-check-square'
                                : 'fa-square'
                            "
                          ></i>
                          Engorgement
                        </button>
                        <button
                          type="button"
                          class="btn text-start"
                          :class="
                            assessment.breast.redness
                              ? 'btn-danger'
                              : 'btn-outline-secondary'
                          "
                          @click="
                            assessment.breast.redness = !assessment.breast
                              .redness
                          "
                        >
                          <i
                            class="fas"
                            :class="
                              assessment.breast.redness
                                ? 'fa-check-square'
                                : 'fa-square'
                            "
                          ></i>
                          Redness/Erythema
                        </button>
                        <button
                          type="button"
                          class="btn text-start"
                          :class="
                            assessment.breast.tenderness
                              ? 'btn-warning'
                              : 'btn-outline-secondary'
                          "
                          @click="
                            assessment.breast.tenderness = !assessment.breast
                              .tenderness
                          "
                        >
                          <i
                            class="fas"
                            :class="
                              assessment.breast.tenderness
                                ? 'fa-check-square'
                                : 'fa-square'
                            "
                          ></i>
                          Tenderness
                        </button>
                        <button
                          type="button"
                          class="btn text-start"
                          :class="
                            assessment.breast.cracks
                              ? 'btn-danger'
                              : 'btn-outline-secondary'
                          "
                          @click="
                            assessment.breast.cracks = !assessment.breast.cracks
                          "
                        >
                          <i
                            class="fas"
                            :class="
                              assessment.breast.cracks
                                ? 'fa-check-square'
                                : 'fa-square'
                            "
                          ></i>
                          Nipple Cracks/Fissures
                        </button>
                      </div>
                    </div>
                    <div class="form-group">
                      <label>Milk Production</label>
                      <select
                        class="form-select"
                        v-model="assessment.breast.milkProduction"
                      >
                        <option value="adequate">Adequate</option>
                        <option value="insufficient">Insufficient</option>
                        <option value="excessive">Excessive</option>
                        <option value="none">None</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Wound/Episiotomy Status -->
              <div class="col-md-6">
                <div class="assessment-item">
                  <h4 class="section-title">
                    <i class="fas fa-cut"></i> Wound/Episiotomy Status
                  </h4>
                  <div class="assessment-grid">
                    <div class="form-group">
                      <label>Wound Type</label>
                      <select
                        class="form-select"
                        v-model="assessment.wound.type"
                      >
                        <option value="episiotomy">Episiotomy</option>
                        <option value="laceration">Laceration</option>
                        <option value="cesarean">Cesarean Section</option>
                        <option value="none">No Wound</option>
                      </select>
                    </div>
                    <div class="form-group">
                      <label>Healing</label>
                      <div class="btn-group w-100" role="group">
                        <button
                          type="button"
                          class="btn"
                          :class="
                            assessment.wound.healing === 'good'
                              ? 'btn-success'
                              : 'btn-outline-success'
                          "
                          @click="assessment.wound.healing = 'good'"
                        >
                          Good
                        </button>
                        <button
                          type="button"
                          class="btn"
                          :class="
                            assessment.wound.healing === 'delayed'
                              ? 'btn-warning'
                              : 'btn-outline-warning'
                          "
                          @click="assessment.wound.healing = 'delayed'"
                        >
                          Delayed
                        </button>
                        <button
                          type="button"
                          class="btn"
                          :class="
                            assessment.wound.healing === 'poor'
                              ? 'btn-danger'
                              : 'btn-outline-danger'
                          "
                          @click="assessment.wound.healing = 'poor'"
                        >
                          Poor
                        </button>
                      </div>
                    </div>
                    <div class="form-group">
                      <label>Signs of Infection</label>
                      <div class="btn-group-vertical w-100" role="group">
                        <button
                          type="button"
                          class="btn text-start"
                          :class="
                            assessment.wound.signsOfInfection.includes(
                              'redness'
                            )
                              ? 'btn-danger'
                              : 'btn-outline-secondary'
                          "
                          @click="toggleInfectionSign('redness')"
                        >
                          <i
                            class="fas"
                            :class="
                              assessment.wound.signsOfInfection.includes(
                                'redness'
                              )
                                ? 'fa-check-square'
                                : 'fa-square'
                            "
                          ></i>
                          Redness
                        </button>
                        <button
                          type="button"
                          class="btn text-start"
                          :class="
                            assessment.wound.signsOfInfection.includes(
                              'swelling'
                            )
                              ? 'btn-danger'
                              : 'btn-outline-secondary'
                          "
                          @click="toggleInfectionSign('swelling')"
                        >
                          <i
                            class="fas"
                            :class="
                              assessment.wound.signsOfInfection.includes(
                                'swelling'
                              )
                                ? 'fa-check-square'
                                : 'fa-square'
                            "
                          ></i>
                          Swelling
                        </button>
                        <button
                          type="button"
                          class="btn text-start"
                          :class="
                            assessment.wound.signsOfInfection.includes(
                              'discharge'
                            )
                              ? 'btn-danger'
                              : 'btn-outline-secondary'
                          "
                          @click="toggleInfectionSign('discharge')"
                        >
                          <i
                            class="fas"
                            :class="
                              assessment.wound.signsOfInfection.includes(
                                'discharge'
                              )
                                ? 'fa-check-square'
                                : 'fa-square'
                            "
                          ></i>
                          Discharge
                        </button>
                        <button
                          type="button"
                          class="btn text-start"
                          :class="
                            assessment.wound.signsOfInfection.includes('odor')
                              ? 'btn-danger'
                              : 'btn-outline-secondary'
                          "
                          @click="toggleInfectionSign('odor')"
                        >
                          <i
                            class="fas"
                            :class="
                              assessment.wound.signsOfInfection.includes('odor')
                                ? 'fa-check-square'
                                : 'fa-square'
                            "
                          ></i>
                          Foul Odor
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Right Column: Mental Health & Summary -->
      <div class="col-lg-4">
        <!-- Mental Health Screening Card -->
        <div class="card mb-4">
          <div class="card-header bg-purple text-white">
            <h3 class="mb-0">
              <i class="fas fa-brain"></i> Mental Health Screening (EPDS)
            </h3>
          </div>
          <div class="card-body">
            <div class="assessment-item">
              <h4 class="section-title">
                Edinburgh Postnatal Depression Scale
              </h4>
              <div class="epds-grid">
                <div
                  v-for="(question, index) in mentalHealth.epdsQuestions"
                  :key="index"
                  class="epds-question"
                >
                  <p class="question-text">{{ question.text }}</p>
                  <div class="btn-group w-100" role="group">
                    <button
                      type="button"
                      class="btn btn-sm"
                      :class="
                        question.score === 0
                          ? 'btn-primary'
                          : 'btn-outline-primary'
                      "
                      @click="setEPDSScore(index, 0)"
                    >
                      {{ question.options[0] }}
                    </button>
                    <button
                      type="button"
                      class="btn btn-sm"
                      :class="
                        question.score === 1
                          ? 'btn-warning'
                          : 'btn-outline-warning'
                      "
                      @click="setEPDSScore(index, 1)"
                    >
                      {{ question.options[1] }}
                    </button>
                    <button
                      type="button"
                      class="btn btn-sm"
                      :class="
                        question.score === 2
                          ? 'btn-warning'
                          : 'btn-outline-warning'
                      "
                      @click="setEPDSScore(index, 2)"
                    >
                      {{ question.options[2] }}
                    </button>
                    <button
                      type="button"
                      class="btn btn-sm"
                      :class="
                        question.score === 3
                          ? 'btn-danger'
                          : 'btn-outline-danger'
                      "
                      @click="setEPDSScore(index, 3)"
                    >
                      {{ question.options[3] }}
                    </button>
                  </div>
                </div>
              </div>

              <!-- EPDS Results -->
              <div class="epds-results mt-4">
                <div class="result-card" :class="epdsResult.class">
                  <div class="result-header">
                    <h5>
                      <i :class="epdsResult.icon"></i> EPDS Score:
                      {{ totalEPDSScore }}
                    </h5>
                  </div>
                  <div class="result-body">
                    <p>
                      <strong>Interpretation:</strong> {{ epdsResult.text }}
                    </p>
                    <p><strong>Risk Level:</strong> {{ epdsResult.risk }}</p>
                    <p>
                      <strong>Recommendation:</strong>
                      {{ epdsResult.recommendation }}
                    </p>
                  </div>
                </div>
              </div>

              <!-- Additional Mental Health Notes -->
              <div class="form-group mt-4">
                <label for="mentalHealthNotes">Additional Notes</label>
                <textarea
                  id="mentalHealthNotes"
                  class="form-control"
                  v-model="mentalHealth.additionalNotes"
                  rows="3"
                  placeholder="Additional observations or concerns..."
                ></textarea>
              </div>
            </div>
          </div>
        </div>

        <!-- Assessment Summary Card -->
        <div class="card">
          <div class="card-header bg-success text-white">
            <h3 class="mb-0">
              <i class="fas fa-clipboard-check"></i> Assessment Summary
            </h3>
          </div>
          <div class="card-body">
            <div class="summary-grid">
              <div class="summary-item" :class="getSummaryStatus('vitalSigns')">
                <div class="summary-icon">
                  <i class="fas fa-heartbeat"></i>
                </div>
                <div class="summary-content">
                  <h6>Vital Signs</h6>
                  <small>{{ vitalSignsSummary }}</small>
                </div>
              </div>

              <div
                class="summary-item"
                :class="getSummaryStatus('uterineLochia')"
              >
                <div class="summary-icon">
                  <i class="fas fa-female"></i>
                </div>
                <div class="summary-content">
                  <h6>Uterine & Lochia</h6>
                  <small>{{ uterineLochiaSummary }}</small>
                </div>
              </div>

              <div
                class="summary-item"
                :class="getSummaryStatus('breastWound')"
              >
                <div class="summary-icon">
                  <i class="fas fa-heart"></i>
                </div>
                <div class="summary-content">
                  <h6>Breast & Wound</h6>
                  <small>{{ breastWoundSummary }}</small>
                </div>
              </div>

              <div
                class="summary-item"
                :class="getSummaryStatus('mentalHealth')"
              >
                <div class="summary-icon">
                  <i class="fas fa-brain"></i>
                </div>
                <div class="summary-content">
                  <h6>Mental Health</h6>
                  <small>{{ mentalHealthSummary }}</small>
                </div>
              </div>
            </div>

            <!-- Overall Status -->
            <div class="overall-status mt-4">
              <div class="status-indicator" :class="overallStatus.class">
                <i :class="overallStatus.icon"></i>
                <span>{{ overallStatus.text }}</span>
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="action-buttons mt-4">
              <button
                class="btn btn-primary w-100 mb-2"
                @click="saveAssessment"
              >
                <i class="fas fa-save"></i> Save Assessment
              </button>
              <button
                class="btn btn-success w-100 mb-2"
                @click="submitAssessment"
              >
                <i class="fas fa-paper-plane"></i> Submit for Review
              </button>
              <button
                class="btn btn-outline-danger w-100"
                @click="resetAssessment"
              >
                <i class="fas fa-redo"></i> Reset Form
              </button>
            </div>

            <!-- Last Updated -->
            <div class="last-updated mt-3 text-center">
              <small class="text-muted">
                <i class="far fa-clock"></i>
                Last updated: {{ lastUpdated }}
              </small>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import pnc_full_head from "./mch/pnc/pnc_full_head.vue";

export default {
  name: "MaternalAssessment",
  components: { pnc_full_head },
  data() {
    return {
      assessment: {
        vitalSigns: {
          bloodPressure: {
            systolic: 120,
            diastolic: 80,
            lastReading: "2024-01-15 10:30",
          },
          temperature: {
            value: 36.5,
            route: "oral",
            lastReading: "2024-01-15 10:30",
          },
          heartRate: 72,
          respiratoryRate: 16,
          weight: 65.5,
        },
        uterineInvolution: {
          fundalHeight: 16,
          position: "midline",
          tenderness: "none",
        },
        lochia: {
          color: "rubra",
          amount: "moderate",
          odor: "normal",
          clots: "none",
        },
        breast: {
          engorgement: false,
          redness: false,
          tenderness: false,
          cracks: false,
          milkProduction: "adequate",
        },
        wound: {
          type: "episiotomy",
          healing: "good",
          signsOfInfection: [],
        },
      },
      mentalHealth: {
        epdsQuestions: [
          {
            text: "I have been able to laugh and see the funny side of things",
            options: [
              "As much as always",
              "Not quite so much",
              "Definitely not so much",
              "Not at all",
            ],
            score: 0,
          },
          {
            text: "I have looked forward with enjoyment to things",
            options: [
              "As much as ever",
              "Rather less than I used to",
              "Definitely less than I used to",
              "Hardly at all",
            ],
            score: 0,
          },
          {
            text: "I have blamed myself unnecessarily when things went wrong",
            options: [
              "No, never",
              "Not very often",
              "Yes, some of the time",
              "Yes, most of the time",
            ],
            score: 0,
          },
          {
            text: "I have been anxious or worried for no good reason",
            options: [
              "No, not at all",
              "Hardly ever",
              "Yes, sometimes",
              "Yes, very often",
            ],
            score: 0,
          },
          {
            text: "I have felt scared or panicky for no very good reason",
            options: [
              "No, not at all",
              "No, not much",
              "Yes, sometimes",
              "Yes, quite a lot",
            ],
            score: 0,
          },
          {
            text: "Things have been getting on top of me",
            options: [
              "No, I have been coping as well as ever",
              "No, most of the time I have coped quite well",
              "Yes, sometimes I haven't been coping as well as usual",
              "Yes, most of the time I haven't been able to cope at all",
            ],
            score: 0,
          },
          {
            text: "I have been so unhappy that I have had difficulty sleeping",
            options: [
              "No, not at all",
              "Not very often",
              "Yes, sometimes",
              "Yes, most of the time",
            ],
            score: 0,
          },
          {
            text: "I have felt sad or miserable",
            options: [
              "No, not at all",
              "Not very often",
              "Yes, quite often",
              "Yes, most of the time",
            ],
            score: 0,
          },
          {
            text: "I have been so unhappy that I have been crying",
            options: [
              "No, never",
              "Only occasionally",
              "Yes, quite often",
              "Yes, most of the time",
            ],
            score: 0,
          },
          {
            text: "The thought of harming myself has occurred to me",
            options: ["Never", "Hardly ever", "Sometimes", "Yes, quite often"],
            score: 0,
          },
        ],
        additionalNotes: "",
      },
      lastUpdated: new Date().toLocaleString(),
    };
  },
  computed: {
    bloodPressureStatus() {
      const sys = this.assessment.vitalSigns.bloodPressure.systolic;
      const dia = this.assessment.vitalSigns.bloodPressure.diastolic;

      if (!sys || !dia)
        return {
          class: "secondary",
          icon: "fas fa-question-circle",
          text: "Not measured",
        };

      if (sys >= 140 || dia >= 90)
        return {
          class: "danger",
          icon: "fas fa-exclamation-triangle",
          text: "High",
        };
      if (sys <= 90 || dia <= 60)
        return { class: "warning", icon: "fas fa-info-circle", text: "Low" };
      return { class: "success", icon: "fas fa-check-circle", text: "Normal" };
    },

    temperatureStatus() {
      const temp = this.assessment.vitalSigns.temperature.value;
      if (!temp)
        return {
          class: "secondary",
          icon: "fas fa-question-circle",
          text: "Not measured",
        };

      if (temp >= 38.0)
        return {
          class: "danger",
          icon: "fas fa-exclamation-triangle",
          text: "Fever",
        };
      if (temp <= 35.5)
        return { class: "warning", icon: "fas fa-info-circle", text: "Low" };
      return { class: "success", icon: "fas fa-check-circle", text: "Normal" };
    },

    totalEPDSScore() {
      return this.mentalHealth.epdsQuestions.reduce(
        (total, question) => total + question.score,
        0
      );
    },

    epdsResult() {
      const score = this.totalEPDSScore;

      if (score <= 9) {
        return {
          class: "success",
          icon: "fas fa-smile",
          text: "No significant depressive symptoms",
          risk: "Low",
          recommendation: "Continue routine follow-up",
        };
      } else if (score <= 12) {
        return {
          class: "warning",
          icon: "fas fa-meh",
          text: "Possible minor depression",
          risk: "Moderate",
          recommendation: "Monitor closely, consider counseling",
        };
      } else {
        return {
          class: "danger",
          icon: "fas fa-frown",
          text: "Probable major depression",
          risk: "High",
          recommendation: "Refer to mental health specialist immediately",
        };
      }
    },

    vitalSignsSummary() {
      const bp = this.bloodPressureStatus.text;
      const temp = this.temperatureStatus.text;
      return `BP: ${bp}, Temp: ${temp}`;
    },

    uterineLochiaSummary() {
      const fundal = this.assessment.uterineInvolution.fundalHeight;
      const lochia = this.assessment.lochia.color;
      return `Fundus: ${fundal}w, Lochia: ${lochia}`;
    },

    breastWoundSummary() {
      const breast = this.assessment.breast.milkProduction;
      const wound = this.assessment.wound.healing;
      return `Milk: ${breast}, Healing: ${wound}`;
    },

    mentalHealthSummary() {
      return `EPDS: ${this.totalEPDSScore} - ${this.epdsResult.risk} Risk`;
    },

    overallStatus() {
      // Simple logic for overall status
      const warnings = [
        this.bloodPressureStatus.class === "danger",
        this.temperatureStatus.class === "danger",
        this.totalEPDSScore >= 12,
        this.assessment.wound.signsOfInfection.length > 0,
      ].filter(Boolean).length;

      if (warnings >= 2) {
        return {
          class: "danger",
          icon: "fas fa-exclamation-triangle",
          text: "Urgent Attention Required",
        };
      } else if (warnings >= 1) {
        return {
          class: "warning",
          icon: "fas fa-info-circle",
          text: "Needs Monitoring",
        };
      } else {
        return {
          class: "success",
          icon: "fas fa-check-circle",
          text: "Normal Assessment",
        };
      }
    },
  },
  methods: {
    toggleInfectionSign(sign) {
      const index = this.assessment.wound.signsOfInfection.indexOf(sign);
      if (index > -1) {
        this.assessment.wound.signsOfInfection.splice(index, 1);
      } else {
        this.assessment.wound.signsOfInfection.push(sign);
      }
    },

    setEPDSScore(index, score) {
      this.mentalHealth.epdsQuestions[index].score = score;
    },

    getSummaryStatus(category) {
      switch (category) {
        case "vitalSigns":
          return this.bloodPressureStatus.class === "success" &&
            this.temperatureStatus.class === "success"
            ? "success"
            : "warning";
        case "uterineLochia":
          return this.assessment.uterineInvolution.tenderness === "none" &&
            this.assessment.lochia.odor === "normal"
            ? "success"
            : "warning";
        case "breastWound":
          return this.assessment.wound.signsOfInfection.length === 0
            ? "success"
            : "warning";
        case "mentalHealth":
          return this.totalEPDSScore <= 9 ? "success" : "warning";
        default:
          return "secondary";
      }
    },

    saveAssessment() {
      this.lastUpdated = new Date().toLocaleString();
      alert("Assessment saved successfully!");
      // In a real app, you would make an API call here
    },

    submitAssessment() {
      if (confirm("Are you ready to submit this assessment for review?")) {
        this.lastUpdated = new Date().toLocaleString();
        alert("Assessment submitted for review!");
        // In a real app, you would make an API call here
      }
    },

    resetAssessment() {
      if (confirm("Are you sure you want to reset all assessment data?")) {
        // Reset all assessment data to defaults
        this.assessment.vitalSigns.bloodPressure.systolic = 120;
        this.assessment.vitalSigns.bloodPressure.diastolic = 80;
        this.assessment.vitalSigns.temperature.value = 36.5;
        this.assessment.vitalSigns.heartRate = 72;
        this.assessment.vitalSigns.respiratoryRate = 16;
        this.assessment.vitalSigns.weight = 65.5;

        this.assessment.uterineInvolution.fundalHeight = 16;
        this.assessment.uterineInvolution.position = "midline";
        this.assessment.uterineInvolution.tenderness = "none";

        this.assessment.lochia.color = "rubra";
        this.assessment.lochia.amount = "moderate";
        this.assessment.lochia.odor = "normal";
        this.assessment.lochia.clots = "none";

        this.assessment.breast.engorgement = false;
        this.assessment.breast.redness = false;
        this.assessment.breast.tenderness = false;
        this.assessment.breast.cracks = false;
        this.assessment.breast.milkProduction = "adequate";

        this.assessment.wound.type = "episiotomy";
        this.assessment.wound.healing = "good";
        this.assessment.wound.signsOfInfection = [];

        this.mentalHealth.epdsQuestions.forEach((q) => (q.score = 0));
        this.mentalHealth.additionalNotes = "";

        this.lastUpdated = new Date().toLocaleString();
        alert("Assessment form has been reset.");
      }
    },
  },
};
</script>

<style scoped>
#maternal-assessment {
  padding: 20px;
  background-color: #f8f9fa;
  min-height: 100vh;
}

.main-content {
  margin-top: 20px;
}

.card {
  border: none;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.card-header {
  border-radius: 12px 12px 0 0 !important;
  padding: 18px 24px;
  font-size: 1.1rem;
}

.bg-purple {
  background: linear-gradient(135deg, #6a11cb 0%, #2575fc 100%) !important;
}

.card-body {
  padding: 24px;
}

.section-title {
  color: #2c3e50;
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 18px;
  padding-bottom: 10px;
  border-bottom: 2px solid #e9ecef;
  display: flex;
  align-items: center;
  gap: 8px;
}

.section-title i {
  color: #4f6d9c;
}

/* Assessment Item Styles */
.assessment-item {
  margin-bottom: 20px;
}

.vitals-grid,
.assessment-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 15px;
}

.vital-input-group {
  margin-bottom: 10px;
}

.vital-input-group label {
  font-weight: 600;
  color: #495057;
  margin-bottom: 5px;
  display: block;
  font-size: 0.9rem;
}

.vital-status {
  padding: 8px 12px;
  border-radius: 8px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
}

.vital-status.success {
  background-color: #d4edda;
  color: #155724;
  border-left: 4px solid #28a745;
}

.vital-status.warning {
  background-color: #fff3cd;
  color: #856404;
  border-left: 4px solid #ffc107;
}

.vital-status.danger {
  background-color: #f8d7da;
  color: #721c24;
  border-left: 4px solid #dc3545;
}

.vital-status.secondary {
  background-color: #e9ecef;
  color: #495057;
  border-left: 4px solid #6c757d;
}

/* Vital Cards */
.vital-card {
  background: white;
  border: 1px solid #e9ecef;
  border-radius: 10px;
  padding: 15px;
  display: flex;
  align-items: center;
  gap: 15px;
  transition: all 0.3s ease;
}

.vital-card:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.vital-icon {
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.2rem;
}

.vital-content {
  flex: 1;
}

.vital-content label {
  font-weight: 600;
  color: #495057;
  margin-bottom: 5px;
  display: block;
  font-size: 0.9rem;
}

/* Form Groups */
.form-group {
  margin-bottom: 15px;
}

.form-group label {
  font-weight: 600;
  color: #495057;
  margin-bottom: 8px;
  display: block;
  font-size: 0.9rem;
}

.btn-group-vertical .btn {
  margin-bottom: 5px;
  text-align: left;
  padding: 10px 15px;
  border-radius: 6px !important;
}

.btn-group .btn {
  padding: 8px 12px;
  font-size: 0.9rem;
}

/* EPDS Styles */
.epds-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 15px;
  max-height: 400px;
  overflow-y: auto;
  padding-right: 10px;
}

.epds-question {
  background: #f8f9fa;
  padding: 15px;
  border-radius: 8px;
  border-left: 4px solid #4f6d9c;
}

.question-text {
  font-weight: 500;
  color: #2c3e50;
  margin-bottom: 10px;
  font-size: 0.95rem;
}

.epds-question .btn-group {
  flex-wrap: wrap;
}

.epds-question .btn {
  font-size: 0.8rem;
  padding: 6px 8px;
  white-space: normal;
  line-height: 1.2;
}

/* EPDS Results */
.epds-results {
  margin-top: 20px;
}

.result-card {
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.result-card.success {
  border: 2px solid #28a745;
}

.result-card.warning {
  border: 2px solid #ffc107;
}

.result-card.danger {
  border: 2px solid #dc3545;
}

.result-header {
  padding: 15px;
  color: white;
  font-weight: 600;
}

.result-card.success .result-header {
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
}

.result-card.warning .result-header {
  background: linear-gradient(135deg, #ffc107 0%, #ffca2c 100%);
}

.result-card.danger .result-header {
  background: linear-gradient(135deg, #dc3545 0%, #fd7e14 100%);
}

.result-body {
  padding: 15px;
  background: white;
}

.result-body p {
  margin-bottom: 8px;
  font-size: 0.9rem;
}

/* Summary Styles */
.summary-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;
}

.summary-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-radius: 8px;
  border-left: 4px solid;
  background: white;
  transition: all 0.3s ease;
}

.summary-item:hover {
  transform: translateX(5px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.summary-item.success {
  border-color: #28a745;
  background: linear-gradient(to right, #d4edda, white);
}

.summary-item.warning {
  border-color: #ffc107;
  background: linear-gradient(to right, #fff3cd, white);
}

.summary-item.danger {
  border-color: #dc3545;
  background: linear-gradient(to right, #f8d7da, white);
}

.summary-item.secondary {
  border-color: #6c757d;
  background: linear-gradient(to right, #e9ecef, white);
}

.summary-icon {
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1rem;
}

.summary-content {
  flex: 1;
}

.summary-content h6 {
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 4px;
  font-size: 0.95rem;
}

.summary-content small {
  color: #6c757d;
  font-size: 0.85rem;
}

/* Overall Status */
.overall-status {
  padding: 15px;
  border-radius: 10px;
  background: white;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.status-indicator {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 12px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1.1rem;
}

.status-indicator.success {
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  color: white;
}

.status-indicator.warning {
  background: linear-gradient(135deg, #ffc107 0%, #ffca2c 100%);
  color: #212529;
}

.status-indicator.danger {
  background: linear-gradient(135deg, #dc3545 0%, #fd7e14 100%);
  color: white;
}

/* Action Buttons */
.action-buttons .btn {
  padding: 12px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.action-buttons .btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
}

.last-updated {
  padding: 10px;
  background: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e9ecef;
}

/* Scrollbar Styling */
.epds-grid::-webkit-scrollbar {
  width: 6px;
}

.epds-grid::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.epds-grid::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 10px;
}

.epds-grid::-webkit-scrollbar-thumb:hover {
  background: #555;
}

/* Responsive Design */
@media (max-width: 992px) {
  .card-body {
    padding: 18px;
  }

  .section-title {
    font-size: 15px;
  }

  .vital-card {
    flex-direction: column;
    text-align: center;
  }

  .vital-icon {
    margin-bottom: 10px;
  }
}

@media (max-width: 768px) {
  #maternal-assessment {
    padding: 15px;
  }

  .card-header {
    padding: 14px 18px;
    font-size: 1rem;
  }

  .btn-group-vertical .btn,
  .btn-group .btn {
    font-size: 0.85rem;
    padding: 8px 10px;
  }

  .epds-question .btn {
    font-size: 0.75rem;
    padding: 4px 6px;
  }

  .status-indicator {
    font-size: 1rem;
    padding: 10px;
  }
}

@media (max-width: 576px) {
  #maternal-assessment {
    padding: 10px;
  }

  .card-body {
    padding: 16px;
  }

  .section-title {
    font-size: 14px;
  }

  .summary-item {
    flex-direction: column;
    text-align: center;
    gap: 8px;
  }

  .summary-icon {
    margin: 0 auto;
  }

  .action-buttons .btn {
    padding: 10px;
    font-size: 0.9rem;
  }
}
</style>
